import { CheckCircle2, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardTitle } from '@/components/ui/card'

interface Account {
  id: string
  name: string
  email: string
}

export interface AccountSelectorProps {
  show: boolean
  accounts: Array<Account>
  accountsLoading: boolean
  selectedAccountId: string | null
  onClose: () => void
  onSelectAccount: (accountId: string) => void
}

export function AccountSelector({
  show,
  accounts,
  accountsLoading,
  selectedAccountId,
  onClose,
  onSelectAccount,
}: AccountSelectorProps) {
  const { t } = useTranslation()

  if (!show) return null

  return (
    <div className="absolute inset-0 z-[1001] bg-black/50 backdrop-blur-sm">
      <div
        className="absolute inset-x-0 bottom-0 z-[1002] max-h-[60vh] flex flex-col rounded-t-2xl bg-background shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label={t('search.selectAccountTitle')}
      >
        <div className="flex items-center justify-between border-b border-border bg-background p-4 shrink-0">
          <CardTitle className="text-base">
            {t('search.selectAccountTitle')}
          </CardTitle>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Close account selection"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {accountsLoading && (
            <div className="text-center text-muted-foreground py-4">
              {t('common.loading')}...
            </div>
          )}
          {!accountsLoading && accounts.length === 0 && (
            <div className="text-center text-muted-foreground py-4">
              {t('search.noAccountsAvailable')}
            </div>
          )}
          {accounts.map((account) => (
            <Card
              key={account.id}
              className={`cursor-pointer transition-all ${
                selectedAccountId === account.id
                  ? 'ring-2 ring-primary'
                  : 'hover:bg-accent'
              }`}
              onClick={() => {
                onSelectAccount(account.id)
                onClose()
              }}
            >
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">
                        {account.name}
                      </span>
                      {selectedAccountId === account.id && (
                        <CheckCircle2 className="h-4 w-4 text-primary" />
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {account.email}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  )
}
